import { useState, useRef } from 'react';
import { AlertCircle, CheckCircle, FileText, Paperclip, Users, X, Loader2 } from 'lucide-react';
import { ISavV2 } from '@/models/savV2';
import { eurosToCents, formatPrice } from '@/utils/priceHelpers';
import { notifications } from '@/routes/outil-pro/utils/notifications';
import { savApi } from '../api/savApi';
import { DeclarationStep } from './steps/DeclarationStep';

interface GarantDirectCoverageViewProps {
  sav: ISavV2;
  /** Invalide la query du SAV après clôture (passe en terminé). */
  onSuccess: () => void;
}

/**
 * Vue dédiée affichée au GARANT en workflow « prise en charge directe »
 * (garant = réparateur, awaitingGarant sans décision). Remplace, en colonne
 * gauche de la page SAV, le stepper devis/décision/signature (`SavDecisionTab`),
 * hors-sujet dans ce cas. Le garant consulte la fiche sinistre puis saisit
 * lui-même les montants (iso `DirectCoverageStep` gestion, montants internes
 * inclus), téléverse optionnellement la facture, et clôture directement le SAV.
 */
export function GarantDirectCoverageView({
  sav,
  onSuccess,
}: GarantDirectCoverageViewProps) {
  const intervention = sav.currentIntervention;

  // Montants publics (saisis en euros)
  const [beneficiaryAmountEuros, setBeneficiaryAmountEuros] = useState('0');
  const [coveredAmountEuros, setCoveredAmountEuros] = useState('0');
  const [commercialAmountEuros, setCommercialAmountEuros] = useState('0');
  // Montants internes (saisis en euros, optionnels)
  const [internalCoveredAmountEuros, setInternalCoveredAmountEuros] = useState('');
  const [internalCommercialAmountEuros, setInternalCommercialAmountEuros] = useState('');
  const [internalComment, setInternalComment] = useState('');

  // Facture (optionnelle)
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [submitting, setSubmitting] = useState(false);

  // Conversions € → centimes
  const beneficiaryAmountCents = eurosToCents(parseFloat(beneficiaryAmountEuros) || 0);
  const coveredAmountCents = eurosToCents(parseFloat(coveredAmountEuros) || 0);
  const commercialAmountCents = eurosToCents(parseFloat(commercialAmountEuros) || 0);
  const totalCoveredCents = coveredAmountCents + commercialAmountCents;
  const totalAmountCents = beneficiaryAmountCents + totalCoveredCents;

  const internalCoveredAmountCents = internalCoveredAmountEuros
    ? eurosToCents(parseFloat(internalCoveredAmountEuros) || 0)
    : undefined;
  const internalCommercialAmountCents = internalCommercialAmountEuros
    ? eurosToCents(parseFloat(internalCommercialAmountEuros) || 0)
    : undefined;
  const hasInternalAmounts =
    internalCoveredAmountEuros !== '' || internalCommercialAmountEuros !== '';

  const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setInvoiceFile(file);
  };

  const handleRemoveFile = () => {
    setInvoiceFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!sav.id || !intervention?.id) {
      notifications.error('Données manquantes pour la prise en charge.');
      return;
    }
    if (totalAmountCents === 0) {
      notifications.error('Veuillez saisir au moins un montant.');
      return;
    }

    setSubmitting(true);
    try {
      // 1) Upload facture (optionnel) : l'endpoint d'upload crée le document ET
      // l'attache déjà au SAV en VISIBLE (isInternal=false) → téléchargeable sur la page.
      // Pas besoin de renvoyer d'attachmentIds (le serveur ne les accepte plus : IDOR).
      if (invoiceFile) {
        await savApi.uploadDocument(
          sav.id,
          invoiceFile,
          'Facture de prise en charge',
          false,
        );
      }

      // 2) Soumission → clôture du SAV.
      await savApi.submitGarantDirectCoverage(sav.id, intervention.id, {
        beneficiaryAmount: beneficiaryAmountCents,
        coveredAmount: coveredAmountCents,
        commercialAmount: commercialAmountCents > 0 ? commercialAmountCents : undefined,
        internalCoveredAmount: internalCoveredAmountCents,
        internalCommercialAmount: internalCommercialAmountCents,
        internalComment: internalComment || undefined,
      });

      notifications.success('Prise en charge enregistrée. Le SAV est clôturé.');
      onSuccess();
    } catch (error) {
      console.error('Erreur lors de la prise en charge directe garant:', error);
      notifications.error("Erreur lors de l'enregistrement de la prise en charge. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tw-space-y-4">
      {/* Bandeau action requise */}
      <div className="tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-xl tw-p-4">
        <div className="tw-flex tw-items-start tw-gap-3">
          <AlertCircle className="tw-w-6 tw-h-6 tw-text-orange-500 tw-flex-shrink-0 tw-mt-0.5" />
          <div>
            <p className="tw-text-base tw-font-semibold tw-text-orange-900 tw-mb-1">
              Prise en charge directe — action requise
            </p>
            <p className="tw-text-sm tw-text-orange-800 tw-mb-0">
              Vous êtes le réparateur de ce dossier. Renseignez les montants pris en charge
              (et, si besoin, la facture) pour clôturer directement le SAV, sans passer par
              le processus de validation et de signature.
            </p>
          </div>
        </div>
      </div>

      {/* Fiche sinistre : on réutilise le DeclarationStep des SAV normaux
          (dates, kilométrage, symptômes, description panne, localisation + map).
          Les documents de déclaration restent affichés dans la sidebar. */}
      <DeclarationStep sav={sav} completedAt={null} />

      {/* Formulaire de prise en charge */}
      <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
        <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-gray-900 tw-uppercase tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 tw-m-0">
          <CheckCircle className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
          Montants de prise en charge
        </h3>
        <div className="tw-p-4 tw-space-y-6">
          {/* Reste à charge bénéficiaire */}
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
              Reste à charge du bénéficiaire
            </label>
            <div className="tw-relative">
              <input
                type="number"
                step="0.01"
                min="0"
                value={beneficiaryAmountEuros}
                onChange={(e) => setBeneficiaryAmountEuros(e.target.value)}
                className="tw-w-full tw-px-4 tw-py-3 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-affinicar-primary focus:tw-border-affinicar-primary"
                placeholder="0.00"
              />
              <span className="tw-absolute tw-right-4 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-500">€</span>
            </div>
            <p className="tw-text-xs tw-text-gray-500 tw-mt-1">Montant à la charge du bénéficiaire</p>
          </div>

          {/* Montant garanti */}
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
              Montant pris en charge par la garantie
            </label>
            <div className="tw-relative">
              <input
                type="number"
                step="0.01"
                min="0"
                value={coveredAmountEuros}
                onChange={(e) => setCoveredAmountEuros(e.target.value)}
                className="tw-w-full tw-px-4 tw-py-3 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-affinicar-primary focus:tw-border-affinicar-primary"
                placeholder="0.00"
              />
              <span className="tw-absolute tw-right-4 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-500">€</span>
            </div>
            <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
              Montant pris en charge par la garantie contractuelle
            </p>
          </div>

          {/* Geste commercial */}
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
              Geste commercial (optionnel)
            </label>
            <div className="tw-relative">
              <input
                type="number"
                step="0.01"
                min="0"
                value={commercialAmountEuros}
                onChange={(e) => setCommercialAmountEuros(e.target.value)}
                className="tw-w-full tw-px-4 tw-py-3 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-affinicar-primary focus:tw-border-affinicar-primary"
                placeholder="0.00"
              />
              <span className="tw-absolute tw-right-4 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-500">€</span>
            </div>
            <p className="tw-text-xs tw-text-gray-500 tw-mt-1">Montant additionnel offert à titre commercial</p>
          </div>

          {/* Montants internes (pilotage) */}
          <div className="tw-border-t tw-border-dashed tw-border-gray-300 tw-pt-6">
            <h4 className="tw-text-sm tw-font-semibold tw-text-purple-700 tw-mb-1">Coûts internes (pilotage)</h4>
            <p className="tw-text-xs tw-text-gray-500 tw-mb-4">
              Si le coût réel interne diffère du montant communiqué au client, renseignez-le ici. Ces
              montants ne sont pas visibles par le bénéficiaire.
            </p>

            <div className="tw-mb-4">
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                Prise en charge interne (optionnel)
              </label>
              <div className="tw-relative">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={internalCoveredAmountEuros}
                  onChange={(e) => setInternalCoveredAmountEuros(e.target.value)}
                  className="tw-w-full tw-px-4 tw-py-3 tw-border tw-border-purple-200 tw-rounded-lg tw-bg-purple-50/30 focus:tw-ring-2 focus:tw-ring-purple-500 focus:tw-border-purple-500"
                  placeholder={coveredAmountEuros !== '0' ? `${coveredAmountEuros} (= montant public)` : '0.00'}
                />
                <span className="tw-absolute tw-right-4 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-500">€</span>
              </div>
              <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                Coût réel de la prise en charge. Si vide, le montant public sera utilisé.
              </p>
            </div>

            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                Geste commercial interne (optionnel)
              </label>
              <div className="tw-relative">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={internalCommercialAmountEuros}
                  onChange={(e) => setInternalCommercialAmountEuros(e.target.value)}
                  className="tw-w-full tw-px-4 tw-py-3 tw-border tw-border-purple-200 tw-rounded-lg tw-bg-purple-50/30 focus:tw-ring-2 focus:tw-ring-purple-500 focus:tw-border-purple-500"
                  placeholder={commercialAmountEuros !== '0' ? `${commercialAmountEuros} (= montant public)` : '0.00'}
                />
                <span className="tw-absolute tw-right-4 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-500">€</span>
              </div>
              <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                Coût réel du geste commercial. Si vide, le montant public sera utilisé.
              </p>
            </div>
          </div>

          {/* Commentaire interne */}
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
              Commentaire interne (optionnel)
            </label>
            <textarea
              value={internalComment}
              onChange={(e) => setInternalComment(e.target.value)}
              rows={4}
              className="tw-w-full tw-px-4 tw-py-3 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-affinicar-primary focus:tw-border-affinicar-primary"
              placeholder="Notes internes sur cette prise en charge..."
            />
          </div>

          {/* Upload facture (optionnel) */}
          <div className="tw-border-t tw-border-gray-200 tw-pt-6">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
              Facture (optionnel)
            </label>
            {invoiceFile ? (
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-p-3 tw-border tw-border-gray-200 tw-rounded-lg tw-bg-gray-50">
                <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                  <FileText className="tw-w-4 tw-h-4 tw-text-affinicar-primary tw-flex-shrink-0" />
                  <span className="tw-text-sm tw-text-gray-700 tw-truncate">{invoiceFile.name}</span>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  disabled={submitting}
                  className="tw-p-1 tw-text-gray-400 hover:tw-text-red-500 tw-bg-transparent tw-border-none tw-cursor-pointer disabled:tw-opacity-50"
                  title="Retirer la facture"
                >
                  <X className="tw-w-4 tw-h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting}
                className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-text-affinicar-primary tw-border tw-border-dashed tw-border-affinicar-primary/40 tw-rounded-lg hover:tw-bg-affinicar-lighter tw-bg-transparent tw-cursor-pointer disabled:tw-opacity-50"
              >
                <Paperclip className="tw-w-4 tw-h-4" />
                Joindre une facture
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/*"
              onChange={handleSelectFile}
              className="tw-hidden"
            />
            <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
              La facture sera ajoutée aux documents du SAV (visible/téléchargeable).
            </p>
          </div>
        </div>
      </div>

      {/* Récapitulatif */}
      <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
        <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-gray-900 tw-uppercase tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 tw-m-0">
          <Users className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
          Récapitulatif
        </h3>
        <div className="tw-p-4 tw-space-y-3">
          {beneficiaryAmountCents > 0 && (
            <div className="tw-flex tw-justify-between tw-items-center">
              <span className="tw-text-gray-600">Reste à charge bénéficiaire</span>
              <span className="tw-text-lg tw-font-semibold tw-text-red-600">{formatPrice(beneficiaryAmountCents)}</span>
            </div>
          )}
          <div className="tw-flex tw-justify-between tw-items-center">
            <span className="tw-text-gray-600">Montant garanti</span>
            <span className="tw-text-lg tw-font-semibold tw-text-gray-900">{formatPrice(coveredAmountCents)}</span>
          </div>
          {commercialAmountCents > 0 && (
            <div className="tw-flex tw-justify-between tw-items-center">
              <span className="tw-text-gray-600">Geste commercial</span>
              <span className="tw-text-lg tw-font-semibold tw-text-green-600">{formatPrice(commercialAmountCents)}</span>
            </div>
          )}
          <div className="tw-pt-3 tw-border-t tw-border-gray-200">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
              <span className="tw-text-gray-900 tw-font-semibold">Total couvert (public)</span>
              <span className="tw-text-xl tw-font-bold tw-text-green-600">{formatPrice(totalCoveredCents)}</span>
            </div>
            <div className="tw-flex tw-justify-between tw-items-center">
              <span className="tw-text-gray-900 tw-font-semibold">Montant total</span>
              <span className="tw-text-2xl tw-font-bold tw-text-affinicar-primary">{formatPrice(totalAmountCents)}</span>
            </div>
          </div>
          {hasInternalAmounts && (
            <div className="tw-pt-3 tw-border-t tw-border-dashed tw-border-purple-200">
              <p className="tw-text-xs tw-font-semibold tw-text-purple-600 tw-mb-2 tw-uppercase">Coûts internes (pilotage)</p>
              {internalCoveredAmountCents != null && (
                <div className="tw-flex tw-justify-between tw-items-center tw-mb-1">
                  <span className="tw-text-gray-600 tw-text-sm">Prise en charge interne</span>
                  <span className="tw-font-semibold tw-text-purple-700">{formatPrice(internalCoveredAmountCents)}</span>
                </div>
              )}
              {internalCommercialAmountCents != null && (
                <div className="tw-flex tw-justify-between tw-items-center">
                  <span className="tw-text-gray-600 tw-text-sm">Geste commercial interne</span>
                  <span className="tw-font-semibold tw-text-purple-700">{formatPrice(internalCommercialAmountCents)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Action */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting || totalAmountCents === 0}
        className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-6 tw-py-3 tw-bg-green-600 hover:tw-bg-green-700 tw-text-white tw-rounded-lg tw-transition-colors tw-border-none tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 className="tw-w-5 tw-h-5 tw-animate-spin" />
            <span>Enregistrement en cours...</span>
          </>
        ) : (
          <>
            <CheckCircle className="tw-w-5 tw-h-5" />
            <span>Valider la prise en charge</span>
          </>
        )}
      </button>

      <div className="tw-p-4 tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-lg">
        <p className="tw-text-sm tw-text-yellow-800 tw-mb-0">
          <strong>Important :</strong> en validant, le SAV passera au statut « Pris en charge » et
          l'intervention sera clôturée. Cette action est définitive.
        </p>
      </div>
    </div>
  );
}
